import { Button, Field, QCard, Saved, WizardNav } from '@concord/ui';
import { useState } from 'react';
import { usePatchIntake } from '../api';
import type { StepProps } from '../IntakeWizard';
import s from './steps.module.css';

/** INT-04: asks whether the patient wants to give age and sex before asking for them. */
export function AboutStep({ v, back, next, mode }: StepProps) {
  const patch = usePatchIntake(v.id);
  const [dob, setDob] = useState(v.dateOfBirth ?? '');
  const volunteered = v.demographicsVolunteered;
  const ready =
    volunteered === false || (volunteered === true && v.gender !== null && v.dateOfBirth !== null);
  const you = mode === 'staff' ? 'the patient' : 'you';
  return (
    <>
      <QCard
        label="About you"
        title={
          mode === 'staff'
            ? 'Does the patient want to share their age and sex?'
            : 'Would you like to tell the physicians your age and sex?'
        }
        lead={`It is optional. It helps the physicians put the case in context, and ${you} can skip it.`}
      >
        <div className={s.inline} role="radiogroup" aria-label="Share age and sex">
          <label className={s.opt} data-on={volunteered === true || undefined}>
            <input
              type="radio"
              className="sr-only"
              checked={volunteered === true}
              onChange={() => patch.mutate({ demographicsVolunteered: true })}
            />
            <span className={s.r} />
            Yes, add them
          </label>
          <label className={s.opt} data-on={volunteered === false || undefined}>
            <input
              type="radio"
              className="sr-only"
              checked={volunteered === false}
              onChange={() => {
                setDob('');
                patch.mutate({ demographicsVolunteered: false });
              }}
            />
            <span className={s.r} />
            No, skip this
          </label>
        </div>
        {volunteered && (
          <div className={s.row}>
            <div className="field">
              <span className="lbl" id="sex-label">
                Sex
              </span>
              <div className={s.inline} role="radiogroup" aria-labelledby="sex-label">
                {(['female', 'male', 'other'] as const).map((g) => (
                  <label key={g} className={s.opt} data-on={v.gender === g || undefined}>
                    <input
                      type="radio"
                      className="sr-only"
                      checked={v.gender === g}
                      onChange={() => patch.mutate({ demographicsVolunteered: true, gender: g })}
                    />
                    <span className={s.r} />
                    {g === 'other' ? 'Another answer' : g[0]!.toUpperCase() + g.slice(1)}
                  </label>
                ))}
              </div>
            </div>
            <Field label="Date of birth" hint="Used only to show the physicians an age.">
              {(id, describedBy) => (
                <input
                  id={id}
                  type="date"
                  className="input"
                  value={dob}
                  max={new Date().toISOString().slice(0, 10)}
                  aria-describedby={describedBy}
                  onChange={(e) => setDob(e.target.value)}
                  onBlur={() =>
                    dob &&
                    dob !== v.dateOfBirth &&
                    patch.mutate({ demographicsVolunteered: true, dateOfBirth: dob })
                  }
                />
              )}
            </Field>
          </div>
        )}
      </QCard>
      <WizardNav
        back={
          <Button variant="sec" onClick={back}>
            Back to expert panel
          </Button>
        }
        status={ready ? <Saved>Saved</Saved> : null}
        next={
          <Button
            size="lg"
            disabled={!ready && !(volunteered && dob && v.gender)}
            onClick={() => {
              if (volunteered && dob && dob !== v.dateOfBirth) patch.mutate({ dateOfBirth: dob });
              next();
            }}
          >
            Continue to {mode === 'staff' ? 'the history' : 'your history'}
          </Button>
        }
      />
    </>
  );
}
